import React, { useEffect, useState } from 'react';
import hash from 'object-hash';
import saveAs from 'file-saver';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { Box, Card, FlexBox, MuiBox, MuiTable, SvgIcon, Typography, color } from '@kis-technologies/kis-ui';
import MediaTableRow from './MediaTableRow';
import { useStyles } from '../styled';
import { MediaResponse, MediaTableProps, MediaTypes, ResourceRow } from '../types';
import { getMediaSource, getMediaTypeLabel } from '../utils';

const MediaTable = (props: MediaTableProps) => {
  const {
    mediaRecords,
    mediaType,
    onDeleteClick,
    onDownloadClick,
    onEditClick,
    onPreviewClick,
    onOrderChange,
    promoCodes,
    kioskGraphics
  } = props;

  const classes = useStyles({ genreTab: false })();

  const mediaTypeLabel = getMediaTypeLabel(mediaType);
  // promo codes and kiosk graphics have one row per resource (code/slot), whether or not media is uploaded
  const hasResourceRows = mediaType === MediaTypes.PROMO_CODE_IMAGE || mediaType === MediaTypes.KIOSK_GRAPHIC;

  // ----- DRAG AND DROP FUNCTIONALITY (SLIDESHOW ONLY) -----
  const sensors = useSensors(useSensor(PointerSensor));
  const [localImages, setLocalImages] = useState<MediaResponse[]>(mediaRecords || []);
  const [sortColumn, setSortColumn] = useState<string>('label');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [dimensionsMap, setDimensionsMap] = useState<Record<string, { width: number; height: number }>>({});

  useEffect(() => {
    setLocalImages(mediaRecords || []);
  }, [mediaRecords]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleDragEnd = (event: DragEndEvent) => {
    if (!onOrderChange) return;

    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = localImages.findIndex((img) => img.s3Name === active.id);
      const newIndex = localImages.findIndex((img) => img.s3Name === over.id);
      const reordered = arrayMove(localImages, oldIndex, newIndex);
      setLocalImages(reordered);
      onOrderChange(reordered.map((i) => i.s3Name));
    }
  };

  const loadDimensions = (mediaSource: string, id: string) => {
    if (!mediaSource) return;

    const img = new Image();
    img.src = mediaSource;

    img.onload = () => {
      setDimensionsMap((prev) => ({
        ...prev,
        [id]: { width: img.width, height: img.height }
      }));
    };
  };

  const getResourceRows = (): ResourceRow[] => {
    if (mediaType === MediaTypes.PROMO_CODE_IMAGE && promoCodes) {
      return promoCodes.map((code) => {
        const mediaData = mediaRecords.find((r) => r.name === hash(code));
        return { label: code, resourceName: code, mediaData, id: mediaData?.s3Name || code };
      });
    }
    if (mediaType === MediaTypes.KIOSK_GRAPHIC && kioskGraphics) {
      // kiosk graphic media records are named by slot key (not hashed)
      return kioskGraphics.map((slot) => {
        const mediaData = mediaRecords.find((r) => r.name === slot.key);
        return {
          label: slot.label,
          resourceName: slot.key,
          mediaData,
          id: mediaData?.s3Name || slot.key,
          defaultGraphic: slot
        };
      });
    }
    return [];
  };

  const getSortedResourceData = () => {
    const mapped = getResourceRows();

    mapped.forEach(({ mediaData, id }) => {
      const mediaSource = mediaData ? getMediaSource(mediaType, mediaData) : '';
      if (mediaSource && !dimensionsMap[id]) {
        loadDimensions(mediaSource, id);
      }
    });

    return mapped.sort((a, b) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let valA: any;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let valB: any;

      if (sortColumn === 'label') {
        const aVal = a.label ?? '';
        const bVal = b.label ?? '';

        const isNumberA = !isNaN(Number(aVal));
        const isNumberB = !isNaN(Number(bVal));

        if (isNumberA && isNumberB) {
          return sortDirection === 'asc' ? Number(aVal) - Number(bVal) : Number(bVal) - Number(aVal);
        }

        return sortDirection === 'asc'
          ? String(aVal).localeCompare(String(bVal))
          : String(bVal).localeCompare(String(aVal));
      }

      if (sortColumn === 'fileName') {
        valA = a.mediaData?.name || '';
        valB = b.mediaData?.name || '';
        return sortDirection === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
      }

      if (sortColumn === 'size') {
        const dimA = dimensionsMap[a.id];
        const dimB = dimensionsMap[b.id];

        const areaA = dimA ? dimA.width * dimA.height : 0;
        const areaB = dimB ? dimB.width * dimB.height : 0;

        return sortDirection === 'asc' ? areaA - areaB : areaB - areaA;
      }

      return 0;
    });
  };
  const handleSort = (column: string) => {
    let direction: 'asc' | 'desc' = 'asc';

    if (sortColumn === column) {
      direction = sortDirection === 'asc' ? 'desc' : 'asc';
    }

    setSortColumn(column);
    setSortDirection(direction);
  };

  const SortArrow = () => {
    return (
      <Box sx={{ display: 'inline-block', cursor: 'pointer', backgroundColor: 'inherit' }}>
        <SvgIcon name={sortDirection === 'asc' ? 'drop_up' : 'drop_down'} size={15} style={{ marginLeft: '5px' }} />
      </Box>
    );
  };

  // --------------------------------------------------------

  const renderTableRow = (idx: number, mediaRecord?: MediaResponse, resourceRow?: ResourceRow) => {
    const mediaSource = mediaRecord ? getMediaSource(mediaType, mediaRecord) : null;
    const tableRowId = mediaRecord?.s3Name || resourceRow?.resourceName || idx.toString();
    const label = mediaType === MediaTypes.ATTRACT_IMAGE ? mediaRecord?.name : resourceRow?.label;
    // kiosk graphics fall back to the bundled default when no custom image is uploaded
    const defaultGraphic = !mediaRecord ? resourceRow?.defaultGraphic : undefined;
    const previewSource = mediaSource || defaultGraphic?.defaultSrc;

    const getDownloadHandler = () => {
      if (onDownloadClick && mediaRecord) return () => onDownloadClick(mediaRecord);
      if (defaultGraphic) return () => saveAs(defaultGraphic.defaultSrc, defaultGraphic.defaultFileName);
      return undefined;
    };

    return (
      <MediaTableRow
        key={tableRowId}
        enableDragAndDrop={!!onOrderChange} // SLIDESHOW ONLY
        id={tableRowId}
        mediaSource={mediaSource || undefined}
        label={label} // PROMO CODES, KIOSK GRAPHICS AND SLIDESHOW ONLY
        mediaRecord={mediaRecord}
        mediaTypeLabel={mediaTypeLabel}
        onDeleteClick={onDeleteClick && mediaRecord ? () => onDeleteClick(mediaRecord) : undefined}
        onDownloadClick={getDownloadHandler()}
        onEditClick={onEditClick ? () => onEditClick(mediaRecord, resourceRow?.resourceName) : undefined}
        onPreviewClick={onPreviewClick && previewSource ? () => onPreviewClick(previewSource) : undefined}
      />
    );
  };

  const getLabelHeader = () => {
    if (mediaType === MediaTypes.ATTRACT_IMAGE) return 'Slide Name';
    if (mediaType === MediaTypes.KIOSK_GRAPHIC) return 'Graphic Name';
    return 'Promo Code';
  };

  return (
    <FlexBox fullWidth noWrap>
      <Box>
        <MuiBox className={classes.tableDetailsOuterBox}>
          <Card hasBorderRadius raised fullHeight>
            <Box flexOverride="1">
              <MuiTable.TableContainer
                className={
                  hasResourceRows || mediaType === MediaTypes.ATTRACT_IMAGE
                    ? classes.tableContainer
                    : classes.singleRowTableContainer
                }
              >
                <MuiTable.Table stickyHeader>
                  <MuiTable.TableHead>
                    <MuiTable.TableRow className={classes.tableRow}>
                      {/* LABEL (FOR PROMO CODES, KIOSK GRAPHICS AND SLIDESHOW ONLY) */}
                      {(hasResourceRows || mediaType === MediaTypes.ATTRACT_IMAGE) && (
                        <MuiTable.TableCell
                          className={`${classes.tableCell} ${classes.tableHeadCell} ${classes.tableLabelCell}`}
                          onClick={() => hasResourceRows && handleSort('label')}
                          sx={{ cursor: hasResourceRows ? 'pointer' : 'default' }}
                        >
                          {getLabelHeader()}
                          {hasResourceRows && sortColumn === 'label' && <SortArrow />}
                        </MuiTable.TableCell>
                      )}
                      {/* FILE NAME */}
                      <MuiTable.TableCell
                        className={`${classes.tableCell} ${classes.tableHeadCell} ${classes.tableFileNameCell}`}
                        onClick={() => hasResourceRows && handleSort('fileName')}
                        sx={{ cursor: hasResourceRows ? 'pointer' : 'default' }}
                      >
                        File Name
                        {hasResourceRows && sortColumn === 'fileName' && <SortArrow />}
                      </MuiTable.TableCell>
                      {/* DIMENSIONS */}
                      <MuiTable.TableCell
                        className={`${classes.tableCell} ${classes.tableHeadCell} ${classes.tableDimensionsCell}`}
                        onClick={() => hasResourceRows && handleSort('size')}
                        sx={{ cursor: hasResourceRows ? 'pointer' : 'default' }}
                      >
                        Size
                        {hasResourceRows && sortColumn === 'size' && <SortArrow />}
                      </MuiTable.TableCell>
                      {/* ACTIONS */}
                      <MuiTable.TableCell
                        className={`${classes.tableCell} ${classes.tableHeadCell} ${classes.tableActionsCell}`}
                      >
                        Actions
                      </MuiTable.TableCell>
                    </MuiTable.TableRow>
                  </MuiTable.TableHead>
                  {mediaType === MediaTypes.ATTRACT_IMAGE ? (
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                      <SortableContext
                        items={localImages.map((mediaRecord) => mediaRecord.s3Name)}
                        strategy={verticalListSortingStrategy}
                      >
                        <MuiTable.TableBody>
                          {localImages.map((mediaRecord, idx) => renderTableRow(idx, mediaRecord))}
                        </MuiTable.TableBody>
                      </SortableContext>
                    </DndContext>
                  ) : (
                    <MuiTable.TableBody>
                      {promoCodes || kioskGraphics
                        ? getSortedResourceData().map((item, idx) => renderTableRow(idx, item.mediaData, item))
                        : mediaRecords.map((mediaRecord, idx) => renderTableRow(idx, mediaRecord))}
                    </MuiTable.TableBody>
                  )}
                </MuiTable.Table>
              </MuiTable.TableContainer>
            </Box>
          </Card>
        </MuiBox>

        {/* DISCLAIMER (SLIDESHOW ONLY) */}
        {mediaType === MediaTypes.ATTRACT_IMAGE && mediaRecords.length >= 10 && (
          <Box margin="4 0">
            <Typography color={color.RED}>You can have up to 10 images in your slideshow.</Typography>
          </Box>
        )}
      </Box>
    </FlexBox>
  );
};

export default MediaTable;
